import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Pagination,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Add, CloudUpload, Delete } from '@mui/icons-material';
import { bankStatementsAPI, banksAPI, Bank } from '../services/api';

interface BankStatement {
  id: number;
  file_name: string;
  uploaded_at: string;
  status: 'pending' | 'processed' | 'failed';
  bank: { id: number; name: string };
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}





const BankStatementsPage: React.FC = () => {
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statementToDelete, setStatementToDelete] = useState<BankStatement | null>(null);

  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    bank_id: '',
    file: null as File | null,
    password: '',
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statementsRes, banksRes] = await Promise.all([
        bankStatementsAPI.getAll({ page: currentPage }),
        banksAPI.getAll(),
      ]);
      
      // Handle new pagination response format
      if (statementsRes.data.statements) {
        setStatements(statementsRes.data.statements);
        setPagination(statementsRes.data.pagination);
      } else {
        // Fallback for old format (direct array)
        setStatements(statementsRes.data);
        setPagination(null);
      }
      
      // Handle new API response format for banks
      setBanks(banksRes.data.data || banksRes.data || []);

    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      bank_id: '',
      file: null,
      password: '',
    });
    setDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData({ ...formData, file });
    }
  };

  const handleSubmit = async () => {
    if (!formData.bank_id || !formData.file) {
      setError('Please select a bank and upload a file');
      return;
    }

    try {
      setUploading(true);
      setError('');

      // Create bank statement record
      const statementRes = await bankStatementsAPI.create({
        file_name: formData.file.name,
        bank_id: parseInt(formData.bank_id),
      });

      // Upload the file with password if provided
      const uploadRes = await bankStatementsAPI.upload(statementRes.data.id, formData.file, formData.password);
      const { transactions_created = 0, transactions_extracted = 0, message } = uploadRes.data;

      setDialogOpen(false);
      setFormData({ bank_id: '', file: null, password: '' });

      if (transactions_extracted === 0) {
        setSuccessMessage('File processed but no transactions found. Check bank selection and PDF password.');
      } else if (transactions_created === 0) {
        setSuccessMessage(`Found ${transactions_extracted} transactions — all already imported (duplicates skipped).`);
      } else {
        setSuccessMessage(`✅ Imported ${transactions_created} of ${transactions_extracted} transactions.${transactions_created < transactions_extracted ? ` ${transactions_extracted - transactions_created} duplicates skipped.` : ''}`);
      }

      fetchData();
      setTimeout(() => setSuccessMessage(''), 6000);
    } catch (error: any) {
      const errMsg = error.response?.data?.error || 'Failed to upload statement';
      setError(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleReprocess = async (statement: BankStatement) => {
    setError('');
    setSuccessMessage('');
    // Re-open upload dialog for the same bank with file picker
    setFormData({ bank_id: statement.bank.id.toString(), file: null, password: '' });
    setDialogOpen(true);
  };

  const handleDeleteClick = (statement: BankStatement) => {
    setStatementToDelete(statement);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!statementToDelete) return;
    
    try {
      await bankStatementsAPI.delete(statementToDelete.id);
      setError('');
      setSuccessMessage('Bank statement deleted successfully');
      setDeleteDialogOpen(false);
      setStatementToDelete(null);
      fetchData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete bank statement');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setStatementToDelete(null);
  };

  

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Bank Statements</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Upload Statement
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}



      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Bank</TableCell>
              <TableCell>Uploaded At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.map((statement) => (
              <TableRow key={statement.id}>
                <TableCell>{statement.file_name}</TableCell>
                <TableCell>{statement.bank.name}</TableCell>
                <TableCell>
                  {new Date(statement.uploaded_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={statement.status === 'failed' ? '⚠ Failed — click re-upload' : statement.status}
                    color={getStatusColor(statement.status) as any}
                    size="small"
                    onClick={statement.status === 'failed' ? () => handleReprocess(statement) : undefined}
                    sx={statement.status === 'failed' ? { cursor: 'pointer' } : {}}
                  />
                </TableCell>
                <TableCell>
                  {statement.status === 'failed' && (
                    <Tooltip title="Re-upload and reprocess">
                      <IconButton
                        color="warning"
                        onClick={() => handleReprocess(statement)}
                        size="small"
                        sx={{ mr: 0.5 }}
                      >
                        <CloudUpload fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete Bank Statement">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(statement)}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {statements.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No bank statements uploaded yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{' '}
              {pagination.total_count} statements
            </Typography>
            <Pagination
              count={pagination.total_pages}
              page={pagination.current_page}
              onChange={(event, page) => {
                setCurrentPage(page);
              }}
              color="primary"
            />
          </Stack>
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Bank Statement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Bank</InputLabel>
                <Select
                  value={formData.bank_id}
                  label="Select Bank"
                  onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ py: 2 }}
              >
                {formData.file ? formData.file.name : 'Choose CSV or PDF File'}
                <input
                  type="file"
                  hidden
                  accept=".csv,.pdf"
                  onChange={handleFileChange}
                />
              </Button>
              {formData.file && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  File size: {(formData.file.size / 1024).toFixed(2)} KB
                </Typography>
              )}
            </Grid>
            
            {/* Password field for PDF files */}
            {formData.file && formData.file.type === 'application/pdf' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="PDF Password (if protected)"
                  type="password"
                  value={formData.password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password if PDF is password protected"
                  helperText="Leave empty if PDF is not password protected"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.bank_id || !formData.file || uploading}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Bank Statement</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the bank statement "{statementToDelete?.file_name}"?
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

export default BankStatementsPage;
